import CustomTable from "@/components/custom-table";
import { Button } from "@/components/ui/button";
import PlatformBadge from "@/components/ui/common/platform-badge";
import ServiceTypePill from "@/components/ui/common/service-type-pill";
import TroubleshootingInfo from "@/components/ui/common/troubleshooting-info";
import { Progress } from "@/components/ui/progress";
import SearchInput from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { HelpCircle, Plus, Rows3, Waypoints } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Diagram from "../../components/ui/diagram/diagram";
import { useUser } from "../../context/user-context";
import { useGetAllProjects } from "../projects/hooks/projects";
import FilterDropdown from "./components/FilterDropdown";
import GetStarted from "./components/GetStarted";
import VersionHistoryDrawer from "./components/VersionHistoryDrawer";
import { useGetOverview } from "./hooks/overview";
import { useServicesTags } from "./hooks/tags";
import useOverviewStore from "./store/overview";

const columns = [
  {
    title: "Service Name",
    accessor: "title",
  },
  {
    title: "Platform",
    accessor: "platform",
    render: (row) => <PlatformBadge platform={row.platform} />,
  },
  {
    title: "Type",
    accessor: "type",
    render: (row) => <ServiceTypePill type={row.type} />,
    headerRender: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center">
            <span>Type</span>
            <HelpCircle className="size-4 ml-1" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm flex flex-col">
            <div className="inline-flex mt-1">
              <ServiceTypePill type="producer" />
              <p>Producer: Produces data</p>
            </div>
            <div className="inline-flex mt-1">
              <ServiceTypePill type="consumer" />
              <p>Consumer: Consumes data</p>
            </div>
            <div className="inline-flex mt-1">
              <ServiceTypePill type="producer / consumer" />
              <p>Producer / Consumer: Produces and consumes data</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    ),
  },
  {
    title: "Inbound",
    accessor: "inbound",
    render: (row) => (
      <span>
        {row.inbounds.length === 0
          ? "-"
          : `${row.inbounds.length - row.inboundErrors} 
            /
            ${row.inbounds.length}`}
      </span>
    ),
  },
  {
    title: "Outbound",
    accessor: "outbound",
    render: (row) => (
      <span>
        {row.outbounds.length === 0
          ? "-"
          : `${row.outbounds.length - row.outboundErrors} 
            /
            ${row.outbounds.length}`}
      </span>
    ),
  },
  {
    title: "Troubleshooting",
    accessor: "warnings",
    render: (row) => <TroubleshootingInfo {...row} />,
  },
  {
    title: "Tags",
    accessor: "tags",
    render: (row) => "TBD",
  },
  {
    title: "Tags",
    accessor: "tags",
    render: (row) => "TBD",
  },
  {
    title: "Version",
    accessor: "version",
  },
];

const Loading = ({ loading, onComplete = () => {} }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!loading) {
      // Reset progress if loading is complete
      setProgress(0);
      onComplete();
      return;
    }

    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          return 100; // Stay at 100% while waiting for actual completion
        }
        return Math.min(oldProgress + 10, 100); // Increment progress
      });
    }, 200); // Adjust interval duration for desired visual speed

    return () => {
      clearInterval(timer);
    };
  }, [loading]);

  return (
    <div className="flex items-center justify-center h-screen bg-background backdrop-blur-lg">
      <div className="w-3/5 md:w-1/4 text-center">
        <Progress value={progress} className="h-1.5 bg-gray-700" />
        <p className="mt-2 text-sm text-gray-400">
          Fetching all the connections...
        </p>
      </div>
    </div>
  );
};

const ProjectSelect = ({ projects: initialProjects = [], isLoading }) => {
  const { user } = useUser();
  const { selectedProject, setSelectedProject } = useOverviewStore();
  const projects = useMemo(() => {
    return initialProjects.map((project) => ({
      isOwner: project.role === "OWNER",
      ...project,
    }));
  }, [initialProjects]);

  const ownedProjects = projects.filter((project) => project.isOwner);
  const sharedProjects = projects.filter((project) => !project.isOwner);
  if (isLoading) {
    return (
      <Select>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Select a project" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem>
            <Skeleton className="h-8 w-full rounded-sm mb-1" />
            <Skeleton className="h-8 w-full rounded-sm mb-1" />
            <Skeleton className="h-8 w-full rounded-sm" />
          </SelectItem>
        </SelectContent>
      </Select>
    );
  }

  const handleProjectChange = (project) => {
    setSelectedProject(project);
  };
  return (
    <Select onValueChange={handleProjectChange} value={selectedProject}>
      <SelectTrigger className="w-[180px]">
        <SelectValue placeholder="Select a project" />
      </SelectTrigger>
      <SelectContent
        actionButton={
          <Link to="/projects?create=true">
            <Button
              variant="borderless"
              className="w-full bg-[#15171C] rounded-t-0"
              onClick={() => {}}
            >
              Add New Project
              <Plus className="size-4 ml-1" />
            </Button>
          </Link>
        }
      >
        <SelectGroup label="Owner">
          <SelectLabel>Owner</SelectLabel>
          {ownedProjects.map((project) => (
            <SelectItem key={project.id} value={project.id}>
              {project.name}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Collaborator</SelectLabel>
          {sharedProjects.map((project) => (
            <SelectItem key={project.id} value={project.id}>
              {project.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const Overview = () => {
  const [tabValue, setTabValue] = useState("graph");
  const [diagramKey, setDiagramKey] = useState(0); // State to manage key prop
  const [isEmpty, setIsEmpty] = useState(true);

  const { user } = useUser();
  const { getOverview, refreshOverview } = useGetOverview();
  const { tags = [] } = useServicesTags();
  const { selectedProject } = useOverviewStore();
  const {
    services: initialServices,
    loading,
    refreshing,
    setSelectedProject,
    setUserId,
    filters,
  } = useOverviewStore();

  // Rehydrate selected project from this user's namespaced storage on
  // mount and whenever the signed-in user changes.
  useEffect(() => {
    setUserId(user?.id || null);
  }, [user?.id, setUserId]);

  const { projects, loading: projectsLoading } = useGetAllProjects();

  useEffect(() => {
    if (selectedProject) {
      getOverview();
      // getTags();
      setDiagramKey((prev) => prev + 1);
    }
  }, [selectedProject]);

  useEffect(() => {
    setDiagramKey((prev) => prev + 1);
  }, [filters]);

  const services = useMemo(() => {
    const { platforms, serviceTypes, tags: filterTags } = filters;
    return initialServices.filter((service) => {
      if (platforms.length > 0 && !platforms.includes(service.platform)) return false;
      if (serviceTypes.length > 0 && !serviceTypes.includes(service.type)) return false;
      if (filterTags.length > 0 && !filterTags.some((t) => service.tags?.includes(t))) return false;
      return true;
    }).map((service) => {
      const inboundErrorCount = service.inbounds.filter(
        (inbound) => inbound.contractMatchingErrors.length > 0,
      ).length;
      const outboundErrorCount = service.outbounds.filter(
        (outbound) => outbound.contractMatchingErrors.length > 0,
      ).length;
      const totalInboundContractErrorCount = service.inbounds.reduce(
        (acc, inbound) => acc + inbound.contractMatchingErrors.length,
        0,
      );
      const totalOutboundContractErrorCount = service.outbounds.reduce(
        (acc, outbound) => acc + outbound.contractMatchingErrors.length,
        0,
      );
      const totalContractErrorCount =
        totalInboundContractErrorCount + totalOutboundContractErrorCount;

      const ownTags =
        tags
          .filter(
            (tag) => tag.name === service.name || tag.name === service.title,
          )
          .flatMap((tag) => tag.tags) || [];
      return {
        ...service,
        tags: ownTags,
        inboundErrors: inboundErrorCount,
        outboundErrors: outboundErrorCount,
        errors: totalContractErrorCount,
      };
    });
  }, [initialServices, tags, filters]);

  useEffect(() => {
    if (projects && projects.length > 0) {
      setIsEmpty(false);
      const stillExists = projects.some((p) => p.id === selectedProject);
      if (!stillExists) {
        setSelectedProject(projects[0].id);
      }
    }
  }, [projects, selectedProject, setSelectedProject]);

  return (
    <div className="relative w-full">
      {!isEmpty && (
        <div className="flex h-[32px] absolute w-full z-[1]">
          <ProjectSelect projects={projects} isLoading={projectsLoading} />
          <Tabs
            defaultValue="graph"
            className="w-[174px] ml-5 rounded-lg"
            onValueChange={(value) => setTabValue(value)}
          >
            <TabsList className="grid w-full grid-cols-2 gap-0">
              <TabsTrigger value="graph">
                <Waypoints className="size-4 mr-2" />
                Graph
              </TabsTrigger>
              <TabsTrigger value="list">
                <Rows3 className="size-4 mr-2" />
                List
              </TabsTrigger>
            </TabsList>
            <TabsContent value="graph"></TabsContent>
            <TabsContent value="list"></TabsContent>
          </Tabs>
          <div className="ml-auto flex items-center space-x-2 ">
            <SearchInput />
            <FilterDropdown />
            <VersionHistoryDrawer />
          </div>
        </div>
      )}

      {loading ? (
        <Loading loading={loading} />
      ) : isEmpty ? (
        <div className="w-full h-[90vh] flex items-center justify-center">
          <GetStarted />
        </div>
      ) : (
        <div>
          {tabValue === "graph" && (
            <ReactFlowProvider>
              <Diagram
                key={diagramKey}
                services={services}
                userId={user?.id}
                projectId={selectedProject}
                onRefresh={refreshOverview}
                refreshing={refreshing}
              />
            </ReactFlowProvider>
          )}
          {tabValue === "list" && (
            <div className="mt-14">
              <CustomTable columns={columns} data={services} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Overview;
